import React from 'react'
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Paper,
  Skeleton,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  AddCircleOutline,
  Dashboard,
  DeleteOutline,
  JoinFull,
  LibraryAdd,
  LooksOne,
  ViewColumn,
} from '@mui/icons-material'
import { alpha, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import usePrepListStore, { type PrepFilterMode, type PrepListState } from '../../store/prepListStore'
import { getChipColors } from '../../utils/colorByCategory'
import type { PrepCategoryWithCount, PrepDateBasis } from '../../api/prep'
import SearchToggle from './SearchToggle'
import { shortenCategoryLabel } from '../../utils/categoryLabel'
import useUserSettingsStore, {
  type PrepLayout,
} from '../../store/userSettings'
import type { PrepAgeMaxDays } from '../../api/userSettings'
import { shapeBorderRadius } from '../../theme/helpers'
import MyPrepToggleButton from './MyPrepToggleButton'
import AddPrepModal from './AddPrepModal'
import PrepDateFilters from './PrepDateFilters'

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect

interface ControlsClusterProps {
  searchValue: string
  onSearchChange: (value: string) => void
  searchPlaceholder?: string
  searchOpen: boolean
  onSearchOpenChange: (open: boolean) => void
  searchInputRef: React.RefObject<HTMLInputElement | null>
  myOnly: boolean
  onMyPrepToggleStart?: (nextEnabled: boolean) => void
  onMyPrepToggled?: (enabled: boolean) => void
  onMyPrepToggleError?: (error: unknown, nextEnabled: boolean) => void
  getMyPrepEnterSelection?: () => Parameters<PrepListState['enterMyOnly']>[0]
  hasActiveFilters: boolean
  onClearFilters: () => void
  mode: PrepFilterMode
  onModeChange: (event: React.SyntheticEvent<Element, Event>, next: PrepFilterMode | null) => void
  effectiveLayout: PrepLayout
  onLayoutChange: (event: React.SyntheticEvent<Element, Event>, next: PrepLayout | null) => void
  isMdUp: boolean
  myOnlyDisabledForLayout: boolean
  headlineLines: 1 | 2 | 3
  onHeadlineLinesChange: (lines: 1 | 2 | 3) => void
  ageMaxDays: PrepAgeMaxDays
  onAgeMaxDaysChange: (days: PrepAgeMaxDays) => void
  dateBasis: PrepDateBasis
  onDateBasisChange: (basis: PrepDateBasis) => void
  searchIncludeDismissed: boolean
  onSearchIncludeDismissedChange: (flag: boolean) => void
  listShowImages: boolean
  onListShowImagesChange: (flag: boolean) => void
  listShowPreviewText: boolean
  onListShowPreviewTextChange: (flag: boolean) => void
  onAddPrepClick: () => void
  recoveryActive?: boolean
  recoveryDisabled?: boolean
  onRecoveryToggle?: () => void
  myPrepDisabled?: boolean
}

const ControlsCluster: React.FC<ControlsClusterProps> = ({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  searchOpen,
  onSearchOpenChange,
  searchInputRef,
  myOnly,
  onMyPrepToggleStart,
  onMyPrepToggled,
  onMyPrepToggleError,
  getMyPrepEnterSelection,
  hasActiveFilters,
  onClearFilters,
  mode,
  onModeChange,
  effectiveLayout,
  onLayoutChange,
  isMdUp,
  myOnlyDisabledForLayout,
  headlineLines,
  onHeadlineLinesChange,
  ageMaxDays,
  onAgeMaxDaysChange,
  dateBasis,
  onDateBasisChange,
  searchIncludeDismissed,
  onSearchIncludeDismissedChange,
  listShowImages,
  onListShowImagesChange,
  listShowPreviewText,
  onListShowPreviewTextChange,
  onAddPrepClick,
  recoveryActive = false,
  recoveryDisabled = false,
  onRecoveryToggle,
  myPrepDisabled = false,
}) => {
  const searchHasValue = searchValue.trim().length > 0
  const recoveryTooltip = recoveryActive ? 'Close used & dismissed view' : 'Show Used & Dismissed Prep Items'
  const handleHeadlineLinesToggle = React.useCallback(
    (event: React.SyntheticEvent<Element, Event>, value: 1 | 2 | 3 | null) => {
      if (value === null) {
        return
      }
      onHeadlineLinesChange(value)
    },
    [onHeadlineLinesChange],
  )
  const searchExpanded = searchOpen || searchHasValue
  const searchWrapperSx = React.useMemo(
    () => ({
      flexGrow: {
        xs: searchExpanded ? 1 : 0,
        sm: searchExpanded ? 1 : 0,
      },
      flexShrink: 1,
      flexBasis: {
        xs: searchExpanded ? '100%' : 'auto',
        sm: searchExpanded ? '240px' : 'auto',
      },
      minWidth: {
        xs: searchExpanded ? 0 : 'auto',
        sm: searchExpanded ? 0 : 'auto',
      },
    }),
    [searchExpanded],
  )
  const searchToggleSx = React.useMemo(
    () => ({
      flex: searchExpanded ? '1 1 auto' : '0 0 auto',
      width: searchExpanded ? '100%' : 'auto',
    }),
    [searchExpanded],
  )

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        flex: '0 0 auto',
        minWidth: 0,
        maxWidth: { xs: '100%', md: 'min(640px, 50%)' },
      })}
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: theme.spacing(1),
          flex: '0 0 auto',
          minWidth: 0,
        })}
      >
        <Box sx={{ flex: '0 0 auto', minWidth: 0 }}>
          <Tooltip title="Add" enterDelay={300} describeChild>
            <span>
              <IconButton
                aria-label="Add"
                size="medium"
                onClick={onAddPrepClick}
                sx={{
                  minHeight: 32,
                  minWidth: 32,
                }}
              >
                <AddCircleOutline />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box sx={searchWrapperSx}>
          <SearchToggle
            value={searchValue}
            onChange={onSearchChange}
            placeholder={searchPlaceholder}
            open={searchOpen}
            onOpenChange={onSearchOpenChange}
            inputRef={searchInputRef}
            sx={searchToggleSx}
            includeDismissed={searchIncludeDismissed}
            onIncludeDismissedChange={onSearchIncludeDismissedChange}
          />
        </Box>

        <Box sx={{ flex: '0 0 auto', minWidth: 0 }}>
          <MyPrepToggleButton
            onToggleStart={onMyPrepToggleStart}
            onToggled={onMyPrepToggled}
            onToggleError={onMyPrepToggleError}
            getEnterSelection={getMyPrepEnterSelection}
            disabled={myPrepDisabled}
          />
        </Box>
        {onRecoveryToggle ? (
          <Box sx={{ flex: '0 0 auto', minWidth: 0 }}>
            <Tooltip title={recoveryTooltip} enterDelay={300} describeChild>
              <span>
                {recoveryActive ? (
                  <Button
                    aria-label="Close used and dismissed prep items"
                    size="small"
                    onClick={onRecoveryToggle}
                    color="error"
                    variant="contained"
                    disabled={recoveryDisabled}
                    aria-pressed={recoveryActive}
                    startIcon={<DeleteOutline fontSize="small" />}
                    sx={{ minHeight: 32, borderRadius: 999, textTransform: 'none' }}
                  >
                    Close
                  </Button>
                ) : (
                  <IconButton
                    aria-label="Show used and dismissed prep items"
                    size="medium"
                    onClick={onRecoveryToggle}
                    color="default"
                    disabled={recoveryDisabled}
                    aria-pressed={recoveryActive}
                    sx={{ minHeight: 32, minWidth: 32 }}
                  >
                    <DeleteOutline />
                  </IconButton>
                )}
              </span>
            </Tooltip>
          </Box>
        ) : null}

        <Box sx={{ flex: '0 0 auto', minWidth: 0 }}>
          <Chip
            component="button"
            size="small"
            label="Clear"
            aria-label="Clear (clears categories and search)"
            onClick={onClearFilters}
            role="button"
            tabIndex={hasActiveFilters ? 0 : -1}
            disabled={!hasActiveFilters}
            color="error"
            variant="filled"
            sx={{
              borderRadius: 999,
              fontWeight: 600,
              px: 1.5,
              py: 0.25,
              minHeight: 32,
              color: (theme) => theme.palette.getContrastText(theme.palette.error.main),
              '&.Mui-disabled': {
                color: (theme) => theme.palette.action.disabled,
                backgroundColor: (theme) => theme.palette.action.disabledBackground,
              },
              '&.Mui-focusVisible': {
                boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.error.main, 0.32)}`,
              },
              '& .MuiChip-label': {
                lineHeight: '20px',
              },
            }}
          />
        </Box>

      </Box>

      <Box
        sx={(theme) => ({
          display: 'flex',
          flexWrap: 'wrap',
          gap: theme.spacing(1),
          flex: '0 0 auto',
          minWidth: 0,
          alignItems: 'center',
        })}
      >
        <ToggleButtonGroup
          exclusive
          value={mode}
          size="small"
          onChange={onModeChange}
          aria-label="Category filter mode"
          sx={{
            whiteSpace: 'nowrap',
            '& .MuiToggleButton-root': {
              px: 1.25,
              fontWeight: 500,
              minWidth: 36,
            },
          }}
        >
          <Tooltip
            title="Show items that match only the selected category"
            enterDelay={300}
            describeChild
          >
            <ToggleButton value="single" aria-label="Show items that match only the selected category">
              <LooksOne fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip
            title="Show items that match all selected categories"
            enterDelay={300}
            describeChild
          >
            <ToggleButton value="and" aria-label="Show items that match all selected categories">
              <JoinFull fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip
            title="Show items that match any selected category"
            enterDelay={300}
            describeChild
          >
            <ToggleButton value="or" aria-label="Show items that match any selected category">
              <LibraryAdd fontSize="small" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={effectiveLayout}
          onChange={onLayoutChange}
          aria-label="Layout"
          disabled={myOnlyDisabledForLayout}
          sx={{
            whiteSpace: 'nowrap',
            '& .MuiToggleButton-root': {
              px: 1.25,
              fontWeight: 500,
              minWidth: 36,
            },
            opacity: myOnlyDisabledForLayout ? 0.55 : 1,
          }}
        >
           <Tooltip title="Show items in a dashboard grid" enterDelay={300} describeChild>
            <ToggleButton value="dashboard" aria-label="Show items in a dashboard grid">
              <Dashboard fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Show items in a multi-column list" enterDelay={300} describeChild>
            <ToggleButton value="columns" aria-label="Show items in a multi-column list">
              <ViewColumn fontSize="small" />
            </ToggleButton>
          </Tooltip>
         
          <Tooltip
            title={
              isMdUp
                ? 'Show items in a single column list'
                : 'Single layout is available on wider screens'
            }
            enterDelay={300}
            describeChild
          >
            <span>
              <ToggleButton value="single" aria-label="Show items in a single column list" disabled={!isMdUp}>
                <LooksOne fontSize="small" />
              </ToggleButton>
            </span>
          </Tooltip>
        </ToggleButtonGroup>

        <Tooltip title="Show preview text" enterDelay={300} describeChild>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={listShowPreviewText}
                onChange={(_event, checked) => onListShowPreviewTextChange(checked)}
                inputProps={{ 'aria-label': 'Toggle showing item preview text' }}
              />
            }
            label="P"
            sx={{
              mx: 0.25,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.8125rem',
              },
            }}
          />
        </Tooltip>

        <Tooltip title="Show item thumbnails" enterDelay={300} describeChild>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={listShowImages}
                onChange={(_event, checked) => onListShowImagesChange(checked)}
                inputProps={{ 'aria-label': 'Toggle showing item images' }}
              />
            }
            label="I"
            sx={{
              mx: 0.25,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.8125rem',
              },
            }}
          />
        </Tooltip>

        <Tooltip title="Lines per item" enterDelay={300} describeChild>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={headlineLines}
            onChange={handleHeadlineLinesToggle}
            aria-label="Lines per item"
            sx={{
              '& .MuiToggleButton-root': {
                px: 0.75,
                fontWeight: 500,
              },
            }}
          >
            <ToggleButton value={1} aria-label="1 line per item">
              1
            </ToggleButton>
            <ToggleButton value={2} aria-label="2 lines per item">
              2
            </ToggleButton>
            <ToggleButton value={3} aria-label="3 lines per item">
              3
            </ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>

        <PrepDateFilters
          dateBasis={dateBasis}
          onDateBasisChange={onDateBasisChange}
          ageMaxDays={ageMaxDays}
          onAgeMaxDaysChange={onAgeMaxDaysChange}
        />
      </Box>
    </Box>
  )
}

export interface CategoryPillsProps {
  categories: PrepCategoryWithCount[]
  loading?: boolean
  emptyMessage?: string
  searchValue: string
  onSearchChange: (value: string) => void
  searchPlaceholder?: string
  onMyPrepToggleStart?: (nextEnabled: boolean) => void
  onMyPrepToggled?: (enabled: boolean) => void
  onMyPrepToggleError?: (error: unknown, nextEnabled: boolean) => void
  getMyPrepEnterSelection?: () => Parameters<PrepListState['enterMyOnly']>[0]
  onRecoveryToggle?: () => void
  recoveryActive?: boolean
  recoveryDisabled?: boolean
  disableMyPrepToggle?: boolean
  onNotify?: (message: string, severity: 'success' | 'error') => void
}

const CategoryPills: React.FC<CategoryPillsProps> = ({
  categories,
  loading = false,
  emptyMessage,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  onMyPrepToggleStart,
  onMyPrepToggled,
  onMyPrepToggleError,
  getMyPrepEnterSelection,
  onRecoveryToggle,
  recoveryActive,
  recoveryDisabled,
  disableMyPrepToggle,
  onNotify,
}) => {
  const myOnly = usePrepListStore((state) => state.myOnly)
  const selectedCategories = usePrepListStore((state) => state.selectedCategories)
  const mode = usePrepListStore((state) => state.mode)
  const search = usePrepListStore((state) => state.search)
  const searchOpen = usePrepListStore((state) => state.searchOpen)
  const toggleCategory = usePrepListStore((state) => state.toggleCategory)
  const setSelectedCategories = usePrepListStore((state) => state.setSelectedCategories)
  const clearFiltersKeepMode = usePrepListStore((state) => state.clearFiltersKeepMode)
  const setSearchOpen = usePrepListStore((state) => state.setSearchOpen)
  const setMode = usePrepListStore((state) => state.setMode)
  const resetPagination = usePrepListStore((state) => state.resetPagination)
  const myCount = usePrepListStore((state) => state.myCount)
  const myCategoryCounts = usePrepListStore((state) => state.myCategoryCounts)
  const layout = useUserSettingsStore((state) => state.prep.layout)
  const setPrepLayout = useUserSettingsStore((state) => state.setPrepLayout)
  const headlineLines = useUserSettingsStore((state) => state.prep.headlineLines)
  const setPrepHeadlineLines = useUserSettingsStore((state) => state.setPrepHeadlineLines)
  const ageMaxDays = useUserSettingsStore((state) => state.prep.ageMaxDays)
  const setPrepAgeMaxDays = useUserSettingsStore((state) => state.setPrepAgeMaxDays)
  const dateBasis = useUserSettingsStore((state) => state.prep.dateBasis)
  const setPrepDateBasis = useUserSettingsStore((state) => state.setPrepDateBasis)
  const searchIncludeDismissed = useUserSettingsStore((state) => state.prep.searchIncludeDismissed)
  const setPrepSearchIncludeDismissed = useUserSettingsStore(
    (state) => state.setPrepSearchIncludeDismissed,
  )
  const listShowImages = useUserSettingsStore((state) => state.prep.listShowImages)
  const setListShowImages = useUserSettingsStore((state) => state.setListShowImages)
  const listShowPreviewText = useUserSettingsStore((state) => state.prep.listShowPreviewText)
  const setListShowPreviewText = useUserSettingsStore((state) => state.setListShowPreviewText)
  const theme = useTheme()
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'))
  const effectiveLayout: PrepLayout = isMdUp ? layout : 'columns'
  const handleHeadlineLinesChange = React.useCallback(
    (lines: 1 | 2 | 3) => {
      setPrepHeadlineLines(lines)
    },
    [setPrepHeadlineLines],
  )

  const handleAgeMaxDaysChange = React.useCallback(
    (days: PrepAgeMaxDays) => {
      setPrepAgeMaxDays(days)
      resetPagination()
    },
    [resetPagination, setPrepAgeMaxDays],
  )

  const handleDateBasisChange = React.useCallback(
    (basis: PrepDateBasis) => {
      setPrepDateBasis(basis)
      resetPagination()
    },
    [resetPagination, setPrepDateBasis],
  )

  const handleSearchIncludeDismissedChange = React.useCallback(
    (flag: boolean) => {
      setPrepSearchIncludeDismissed(flag)
    },
    [setPrepSearchIncludeDismissed],
  )

  const handleListShowImagesChange = React.useCallback(
    (flag: boolean) => {
      setListShowImages(flag)
    },
    [setListShowImages],
  )

  const handleListShowPreviewTextChange = React.useCallback(
    (flag: boolean) => {
      setListShowPreviewText(flag)
    },
    [setListShowPreviewText],
  )

  const visibleCategories = React.useMemo(() => {
    if (!myOnly) {
      return categories
    }

    return categories.filter((category) => (myCategoryCounts[category.id] ?? 0) > 0)
  }, [categories, myCategoryCounts, myOnly])

  const visibleCategorySlugs = React.useMemo(
    () => visibleCategories.map((category) => category.slug),
    [visibleCategories],
  )

  const visibleSlugSet = React.useMemo(() => new Set(visibleCategorySlugs), [visibleCategorySlugs])

  const hasActiveFilters = selectedCategories.length > 0 || search.trim() !== ''

  const handleModeChange = React.useCallback(
    (_event: React.SyntheticEvent<Element, Event>, next: PrepFilterMode | null) => {
      if (next) {
        setMode(next)
      }
    },
    [setMode],
  )

  const handleLayoutChange = React.useCallback(
    (_event: React.SyntheticEvent<Element, Event>, next: PrepLayout | null) => {
      if (myOnly) {
        return
      }
      if (!next) {
        return
      }
      if (next === 'single' && !isMdUp) {
        return
      }
      if (next === layout) {
        return
      }
      setPrepLayout(next)
      resetPagination()
    },
    [isMdUp, layout, myOnly, resetPagination, setPrepLayout],
  )

  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const lastFocusedSlugRef = React.useRef<string | null>(null)
  const searchInputRef = React.useRef<HTMLInputElement | null>(null)
  const [expanded, setExpanded] = React.useState(false)
  const [collapseHeight, setCollapseHeight] = React.useState<number | null>(null)
  const [overflowed, setOverflowed] = React.useState(false)
  const [addPrepOpen, setAddPrepOpen] = React.useState(false)
  const modalCategories = React.useMemo(
    () =>
      categories
        .filter((category) => typeof category.id === 'number' && category.name.trim().length > 0)
        .map((category) => ({ id: category.id, name: category.name.trim() })),
    [categories],
  )

  const handleAddPrepSuccess = React.useCallback(() => {
    setAddPrepOpen(false)
    onNotify?.('Added', 'success')
  }, [onNotify])

  const handleAddPrepError = React.useCallback(
    (message: string) => {
      onNotify?.(message, 'error')
    },
    [onNotify],
  )

  const handleAddPrepClose = React.useCallback(() => {
    setAddPrepOpen(false)
  }, [])

  const measure = React.useCallback(() => {
    if (typeof window === 'undefined') {
      return
    }

    const container = containerRef.current
    if (!container) {
      setCollapseHeight(null)
      setOverflowed(false)
      return
    }

    const firstChip = container.querySelector<HTMLElement>('[data-prep-chip="true"]')
    if (!firstChip) {
      setCollapseHeight(null)
      setOverflowed(false)
      return
    }

    const chipHeight = firstChip.offsetHeight
    if (chipHeight <= 0) {
      setCollapseHeight(null)
      setOverflowed(false)
      return
    }

    const styles = window.getComputedStyle(container)
    const rowGap = parseFloat(styles.rowGap || styles.gap || '0') || 0
    const targetHeight = chipHeight * 2 + rowGap

    setCollapseHeight((prev) => {
      if (prev !== null && Math.abs(prev - targetHeight) < 0.5) {
        return prev
      }
      return targetHeight
    })

    const totalHeight = container.scrollHeight
    const nextOverflowed = totalHeight > targetHeight + 1
    setOverflowed((prev) => (prev === nextOverflowed ? prev : nextOverflowed))
  }, [])

  useIsomorphicLayoutEffect(() => {
    measure()

    const container = containerRef.current
    if (!container) {
      return
    }

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => measure())
      observer.observe(container)
      return () => observer.disconnect()
    }

    const handleResize = () => measure()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [measure])

  React.useEffect(() => {
    measure()
  }, [visibleCategories, hasActiveFilters, measure, searchOpen, searchValue, loading])

  React.useEffect(() => {
    if (!overflowed && expanded) {
      setExpanded(false)
    }
  }, [expanded, overflowed])

  const showMoreChip = !loading && overflowed

  const moreChipAriaLabel = expanded ? 'Show fewer categories' : 'Show more categories'

  const handleSearchOpenChange = React.useCallback(
    (open: boolean) => {
      setSearchOpen(open)
    },
    [setSearchOpen],
  )

  const showEmptyState = !loading && visibleCategories.length === 0 && !myOnly

  React.useEffect(() => {
    if (!myOnly) {
      return
    }

    if (selectedCategories.length === 0) {
      return
    }

    const filtered = selectedCategories.filter((slug) => visibleSlugSet.has(slug))

    if (filtered.length === selectedCategories.length) {
      return
    }

    setSelectedCategories(filtered)
  }, [myOnly, selectedCategories, setSelectedCategories, visibleSlugSet])

  useIsomorphicLayoutEffect(() => {
    if (!myOnly) {
      lastFocusedSlugRef.current = null
      return
    }

    const lastFocusedSlug = lastFocusedSlugRef.current
    if (!lastFocusedSlug) {
      return
    }

    if (visibleSlugSet.has(lastFocusedSlug)) {
      return
    }

    const nextChip = containerRef.current?.querySelector<HTMLElement>('[data-category-slug]')
    if (nextChip) {
      nextChip.focus()
      return
    }

    const container = containerRef.current
    if (container) {
      container.focus({ preventScroll: true })
    }

    lastFocusedSlugRef.current = null
  }, [myOnly, visibleSlugSet])

  const handleCategoryToggle = React.useCallback(
    (slug: string) => {
      toggleCategory(slug)
    },
    [toggleCategory],
  )

  const handleClearFilters = React.useCallback(() => {
    clearFiltersKeepMode()
  }, [clearFiltersKeepMode])

  const resolvedEmptyMessage = myOnly ? emptyMessage ?? 'No categories in My Prep yet.' : emptyMessage
  return (
    <Paper
      variant="outlined"
      className="rpa-prepFilterBar"
      sx={{
        borderRadius: shapeBorderRadius,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
      aria-label="Prep categories"
      role="toolbar"
    >
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            alignItems: 'flex-start',
            gap: 2,
            px: 0,
            py: 0,
            pr: !expanded && showMoreChip ? 6 : 0,
          }}
        >
          <ControlsCluster
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            searchPlaceholder={searchPlaceholder}
            searchOpen={searchOpen}
            onSearchOpenChange={handleSearchOpenChange}
            searchInputRef={searchInputRef}
            myOnly={myOnly}
            onMyPrepToggleStart={onMyPrepToggleStart}
            onMyPrepToggled={onMyPrepToggled}
            onMyPrepToggleError={onMyPrepToggleError}
            getMyPrepEnterSelection={getMyPrepEnterSelection}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={handleClearFilters}
            mode={mode}
            onModeChange={handleModeChange}
            effectiveLayout={effectiveLayout}
            onLayoutChange={handleLayoutChange}
            isMdUp={isMdUp}
            myOnlyDisabledForLayout={myOnly}
            headlineLines={headlineLines}
            onHeadlineLinesChange={handleHeadlineLinesChange}
            ageMaxDays={ageMaxDays}
            onAgeMaxDaysChange={handleAgeMaxDaysChange}
            dateBasis={dateBasis}
            onDateBasisChange={handleDateBasisChange}
            searchIncludeDismissed={searchIncludeDismissed}
            onSearchIncludeDismissedChange={handleSearchIncludeDismissedChange}
            listShowImages={listShowImages}
            onListShowImagesChange={handleListShowImagesChange}
            listShowPreviewText={listShowPreviewText}
            onListShowPreviewTextChange={handleListShowPreviewTextChange}
            onAddPrepClick={() => setAddPrepOpen(true)}
            recoveryActive={recoveryActive}
            recoveryDisabled={recoveryDisabled}
            onRecoveryToggle={onRecoveryToggle}
            myPrepDisabled={disableMyPrepToggle}
          />

          <Box
            ref={containerRef}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
              overflow: expanded ? 'visible' : 'hidden',
              maxHeight: !expanded && collapseHeight ? `${collapseHeight}px` : 'none',
              transition: 'max-height 180ms ease',
              minWidth: 0,
              flex: '1 1 0%',
            }}
            tabIndex={-1}
          >
            {loading
              ? Array.from({ length: 8 }).map((_, index) => (
              <Skeleton
                key={`loading-${index}`}
                variant="rounded"
                width={96}
                height={32}
                sx={{ borderRadius: 999 }}
              />
            ))
              : visibleCategories.map((category) => {
              const slug = category.slug
              const isSelected = selectedCategories.includes(slug)
              const { label, shortened } = shortenCategoryLabel(category.name)
              const categoryCount = myOnly ? myCategoryCounts[category.id] ?? 0 : category.count
              const chipLabel = `${label} • ${categoryCount}`
              const chipColors = getChipColors(slug, { selected: isSelected })
              const isDisabled = myOnly && categoryCount === 0

              return (
                <Tooltip
                  key={slug}
                  title={shortened ? category.name : undefined}
                  disableHoverListener={!shortened}
                  disableFocusListener={!shortened}
                  disableTouchListener={!shortened}
                >
                  <Chip
                    data-prep-chip="true"
                    data-category-slug={slug}
                    component="button"
                    size="small"
                    label={chipLabel}
                    aria-label={`${category.name}, ${categoryCount}`}
                    aria-pressed={isSelected}
                    onClick={() => {
                      if (isDisabled) {
                        return
                      }
                      handleCategoryToggle(slug)
                    }}
                    onFocus={() => {
                      lastFocusedSlugRef.current = slug
                    }}
                    role="button"
                    tabIndex={isDisabled ? -1 : 0}
                    disabled={isDisabled}
                    sx={{
                      borderRadius: 999,
                      fontWeight: 500,
                      px: 1.25,
                      py: 0.25,
                      minHeight: 32,
                      borderStyle: 'solid',
                      borderWidth: 1,
                      transition: 'background-color 120ms ease, color 120ms ease, border-color 120ms ease',
                      backgroundColor: chipColors.background,
                      color: chipColors.text,
                      borderColor: chipColors.border,
                      opacity: isDisabled ? 0.4 : 1,
                      '&:hover': {
                        backgroundColor: chipColors.hoverBackground,
                        borderColor: chipColors.hoverBorder,
                      },
                      '&.Mui-focusVisible': {
                        boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.32)}`,
                      },
                      '& .MuiChip-label': {
                        lineHeight: '20px',
                      },
                    }}
                  />
                </Tooltip>
              )
            })}
            {!loading && myOnly && visibleCategories.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
                {resolvedEmptyMessage}
              </Typography>
            ) : null}
          </Box>
        </Box>

        {showMoreChip ? (
          <Box
            sx={{
              position: expanded ? 'static' : 'absolute',
              right: expanded ? undefined : 0,
              bottom: expanded ? undefined : 0,
              mt: expanded ? 0.75 : 0,
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
              pointerEvents: 'none',
            }}
          >
            <Chip
              component="button"
              size="small"
              label={expanded ? 'Less' : 'More'}
              aria-label={moreChipAriaLabel}
              aria-expanded={expanded}
              onClick={() => {
                setExpanded((prev) => !prev)
              }}
              role="button"
              tabIndex={0}
              disabled={false}
              sx={{
                borderRadius: 999,
                fontWeight: 500,
                px: 1.25,
                py: 0.25,
                minHeight: 32,
                backgroundColor: 'action.hover',
                color: 'text.primary',
                pointerEvents: 'auto',
                opacity: 1,
                '&.Mui-focusVisible': {
                  boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.32)}`,
                },
                '& .MuiChip-label': {
                  lineHeight: '20px',
                },
              }}
            />
          </Box>
        ) : null}
      </Box>

      {showEmptyState && emptyMessage ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 0.5, py: 0.75 }}>
          {emptyMessage}
        </Typography>
      ) : null}
      <AddPrepModal
        open={addPrepOpen}
        categories={modalCategories}
        onClose={handleAddPrepClose}
        onSubmitSuccess={handleAddPrepSuccess}
        onSubmitError={handleAddPrepError}
      />
    </Paper>
  )
}

export default CategoryPills
